import { RegisterClass } from "core/Misc/typeStore";
import { Quaternion } from "core/Maths/math.vector";
import { PropertyTypeForEdition, editableInPropertyPage } from "core/Decorators/nodeDecorator";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphBuildState } from "../rewardGraphBuildState";

export enum RewardPartQuatPropertyType {
    Rotation,
    DefaultRotation,
    LastRotation
}

function OperationString(operation : RewardPartQuatPropertyType) : string {
    if( typeof operation === 'number' )
        return RewardPartQuatPropertyType[operation];
    return operation as string;
}

function StringOperation(op : string) {
    return (RewardPartQuatPropertyType as any)[op];
}

/**
 * Block used to apply math functions
 */
export class RewardPartPropertyQuat extends RewardGraphNode {
    /**
     * Gets or sets the operation applied by the block
     */
    @editableInPropertyPage("Property", PropertyTypeForEdition.List, "ADVANCED", {
        notifiers: { rebuild: true },
        options: [
            { label: "Rotation", value: RewardPartQuatPropertyType.Rotation },
            { label: "DefaultRotation", value: RewardPartQuatPropertyType.DefaultRotation },
            { label: "LastRotation", value: RewardPartQuatPropertyType.LastRotation },
        ],
    })
    public property = RewardPartQuatPropertyType.Rotation;

    /**
     * Create a new MathBlock
     * @param name defines the block name
     */
    public constructor(name: string) {
        super(name);

        this.registerInput("part", RewardGraphConnectionPointTypes.PartID);
        this.registerOutput("output", RewardGraphConnectionPointTypes.Quat);
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardPartPropertyQuat";
    }

    /**
     * Gets the left input component
     */
    public get part(): RewardGraphConnectionPoint {
        return this._inputs[0];
    }

    /**
     * Gets the reward output component
     */
    public get output(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    protected override _buildBlock() {
        let func: (state: RewardGraphBuildState) => any;

        if (!this.part.isConnected) {
            this.output._storedFunction = null;
            this.output._storedValue = null;
            return;
        }

        switch (this.property) {
            case RewardPartQuatPropertyType.Rotation: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext?.getPartQuatProperty( this.part.getConnectedValue(state), "rotation" );
                        return Quaternion.Identity();
                    };
                break;
            }
            case RewardPartQuatPropertyType.DefaultRotation: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext?.getPartQuatProperty( this.part.getConnectedValue(state), "default-rotation" );
                        return Quaternion.Identity();
                    };
                break;
            }
            case RewardPartQuatPropertyType.LastRotation: {
                    func = (state) => {
                        if( state.queryContext )
                            return state.queryContext?.getPartQuatProperty( this.part.getConnectedValue(state), "last-rotation" );
                        return Quaternion.Identity();
                    };
                break;
            }
        }

        this.output._storedFunction = (state) => {
            return func(state);
        };
    }

    protected override _dumpPropertiesCode() {
        const codeString = super._dumpPropertiesCode() + `${this._codeVariableName}.operation = MELS.RewardPartPropertyQuat.${RewardPartQuatPropertyType[this.property]};\n`;
        return codeString;
    }

    public override serialize(): any {
        const serializationObject = super.serialize();

        serializationObject.property = OperationString(this.property);

        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);

        this.property = StringOperation(serializationObject.property);
    }
}

RegisterClass("MELS.RewardPartPropertyQuat", RewardPartPropertyQuat);
